import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BlogPost as BlogPostType } from '@/services/blog';
import { Badge } from './ui/badge';
import { Calendar, Clock, User, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

interface BlogPostProps {
  post: BlogPostType;
}

export const BlogPost = ({ post }: BlogPostProps) => {
  const navigate = useNavigate();
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-background">
      {/* Hero Section */}
      <div className="relative bg-gradient-subtle border-b border-border/50">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="relative max-w-4xl mx-auto px-6 py-16">
          <Button
            variant="ghost"
            onClick={() => navigate("/blog")}
            className="mb-8 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
          
          <div className="flex items-center gap-3 mb-6">
            <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 transition-colors">
              {post.category}
            </Badge>
            {post.featured && (
              <Badge variant="secondary" className="bg-accent/10 text-accent-foreground border-accent/20">
                Featured
              </Badge>
            )}
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
            {post.title}
          </h1>
          
          <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed max-w-3xl">
            {post.excerpt}
          </p>
          
          <div className="flex items-center gap-8 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-4 h-4 text-primary" />
              </div>
              <span className="font-medium">{post.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary" />
              <span>{formatDate(post.date)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" />
              <span>{post.readTime}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-lg prose-slate dark:prose-invert max-w-none
                        prose-headings:text-foreground prose-headings:font-bold
                        prose-p:text-foreground prose-p:leading-relaxed prose-p:text-lg
                        prose-li:text-foreground prose-li:leading-relaxed
                        prose-strong:text-foreground prose-strong:font-semibold
                        prose-a:text-primary prose-a:font-medium prose-a:no-underline
                        hover:prose-a:text-primary/80 hover:prose-a:underline
                        prose-blockquote:border-l-primary prose-blockquote:border-l-4
                        prose-blockquote:bg-muted/30 prose-blockquote:rounded-r-lg
                        prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:not-italic
                        prose-blockquote:text-foreground
                        prose-code:bg-muted prose-code:text-foreground prose-code:px-2 prose-code:py-1
                        prose-code:rounded prose-code:text-sm prose-code:font-mono
                        prose-pre:bg-muted prose-pre:border prose-pre:border-border
                        prose-h1:text-4xl prose-h1:mb-8 prose-h1:mt-12
                        prose-h2:text-3xl prose-h2:mb-6 prose-h2:mt-10 prose-h2:border-b
                        prose-h2:border-border prose-h2:pb-2
                        prose-h3:text-2xl prose-h3:mb-4 prose-h3:mt-8
                        prose-h4:text-xl prose-h4:mb-3 prose-h4:mt-6
                        prose-ul:space-y-2 prose-ol:space-y-2
                        prose-li:marker:text-primary
                        first:prose-p:text-xl first:prose-p:font-medium first:prose-p:text-muted-foreground">
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={{
              h2: ({ children }) => (
                <h2 className="group">
                  {children}
                  <div className="w-12 h-1 bg-gradient-to-r from-primary to-primary/50 rounded-full mt-2"></div>
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="group">
                  {children}
                  <div className="w-8 h-0.5 bg-primary/60 rounded-full mt-1"></div>
                </h3>
              ),
              blockquote: ({ children }) => (
                <blockquote className="border-l-4 border-primary bg-gradient-to-r from-primary/5 to-transparent pl-6 pr-4 py-4 rounded-r-lg my-6">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <div>{children}</div>
                  </div>
                </blockquote>
              ),
              p: ({ children }) => (
                <p className="mb-6 text-lg leading-relaxed">
                  {children}
                </p>
              ),
              ul: ({ children }) => (
                <ul className="space-y-3 my-6">
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol className="space-y-3 my-6">
                  {children}
                </ol>
              ),
              li: ({ children }) => (
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
                  <div className="flex-1">{children}</div>
                </li>
              ),
              code: ({ children, ...props }) => (
                <code 
                  className="bg-muted text-primary px-2 py-1 rounded text-sm font-mono border border-border/50"
                  {...props}
                >
                  {children}
                </code>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold text-primary">
                  {children}
                </strong>
              )
            }}
          >
            {post.content}
          </ReactMarkdown>
        </div>
        
        {/* Back to Blog CTA */}
        <div className="mt-16 pt-8 border-t border-border">
          <div className="text-center">
            <Button 
              onClick={() => navigate("/blog")}
              className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground shadow-lg"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to All Posts
            </Button>
          </div>
        </div>
      </article>
    </div>
  );
};